<template>
  <PopupInputgroup
    ref="popup"
    class="popup-moneyinput"
    :label="label"
    :append-to="appendTo"
    :disabled="!!disabled"
    :hide-edit-icon="hideEditIcon"
    :invalid="v$.$invalid"
    @cancel="onCancel"
    @focus="onFocus"
    @save="onSave"
  >
    <template #popupContent>
      <div class="flex flex-gap-1">
        <InputWrapper
          v-slot="{ ariaProps, inputClasses, inputId }"
          :input-id="`${id}-amount`"
          :label="label"
          :errors="v$.internalValue.$errors"
        >
          <InputMoney
            :id="inputId"
            ref="input"
            v-model="internalValue"
            :default-currency="defaultCurrency"
            :nullable="nullable"
            :min="min"
            :class="inputClasses"
            :disabled="disabled"
            v-bind="ariaProps"
            @keyup.enter="onSave"
            @keyup.esc="closePopup"
            @keydown.tab.passive="onSave"
          />
        </InputWrapper>

        <InputWrapper
          v-slot="{ ariaProps, inputId }"
          :input-id="`${id}-currency`"
          :label="t('currency.currency')"
          optional
        >
          <CurrencyDropdown
            :id="inputId"
            :model-value="internalValue?.currency"
            v-bind="ariaProps"
            @update:model-value="onCurrencyUpdate"
          />
        </InputWrapper>
      </div>
    </template>

    <template #placeholder="{ label: placeholderLabel }">
      <slot
        name="placeholder"
        :label="placeholderLabel"
        :placeholder="placeholderValue"
      >
        <KeyValuePair
          :class="{ 'no-label': !label }"
          :label="label"
          :value="placeholderValue"
          uppercase-label
        />
      </slot>
    </template>
  </PopupInputgroup>
</template>

<script lang="ts" setup>
import type { ComponentPublicInstance, PropType, Ref } from 'vue';
import { computed, nextTick, ref, toRefs, watch } from 'vue';
import { useI18n } from 'vue-i18n';

import { useValidations } from '@rygen/rygen-ui/composables';
import { renderMoney } from '@rygen/rygen-ui/i18n';
import { Currency, Money } from '@rygen/rygen-ui/model';

import MoneyUtil from '@/util/MoneyUtil';

import CurrencyDropdown from '@rygen/rygen-ui/components/form/CurrencyDropdown.vue';
import InputMoney from '@rygen/rygen-ui/components/form/InputMoney.vue';
import InputWrapper from '@rygen/rygen-ui/components/form/InputWrapper.vue';
import PopupInputgroup from '@rygen/rygen-ui/components/popup/PopupInputgroup.vue';
import KeyValuePair from '@rygen/rygen-ui/components/ui/KeyValuePair.vue';

const props = defineProps({
  id: {
    type: String,
    required: true,
  },
  modelValue: {
    type: Money,
    default: (rawProps: { nullable: boolean }) => {
      return rawProps.nullable ? undefined : Money.zero(Currency.USD);
    },
  },
  label: {
    type: String,
    default: () => '',
  },
  defaultCurrency: {
    type: String as PropType<Currency>,
    default: () => Currency.USD,
  },
  min: {
    type: Number,
    default: undefined,
  },
  validationRules: {
    type: Object as PropType<Record<string, any>>,
    default: () => undefined,
  },
  appendTo: {
    type: String as PropType<'body' | 'self'>,
    default: () => 'self',
  },
  nullable: {
    type: Boolean,
    default: false,
  },
  disabled: {
    type: Boolean,
    default: () => false,
  },
  hideEditIcon: {
    type: Boolean,
    default: () => false,
  },
});
const {
  id,
  modelValue,
  label,
  defaultCurrency,
  min,
  validationRules,
  appendTo,
  nullable,
  disabled,
  hideEditIcon,
} = toRefs(props);

const emit = defineEmits<{
  (event: 'update:model-value', newVal?: Money): void;
}>();

defineSlots<{
  placeholder: (props: { label: string; placeholder?: string }) => any;
}>();

const { t } = useI18n();

const internalValue: Ref<Money | undefined> = ref(
  MoneyUtil.clone(modelValue?.value),
);
const wrappedValue = ref({ internalValue });
const rules = {
  internalValue: validationRules?.value ?? {},
};
const { validate, v$ } = useValidations(wrappedValue, rules, id.value);

const popup: Ref<ComponentPublicInstance | null> = ref(null);
const input: Ref<ComponentPublicInstance | null> = ref(null);

const placeholderValue = computed(() => renderMoney(modelValue.value));

const onCurrencyUpdate = (newVal: string) => {
  if (!internalValue.value) {
    internalValue.value = new Money();
  }

  internalValue.value.currency = newVal as Currency;
};

function onFocus() {
  if (disabled.value) {
    return;
  }

  nextTick(() => input.value?.$el.focus());
}

const onSave = async () => {
  let isValid = validationRules?.value ? await validate() : true;
  if (!isValid) {
    return;
  }

  if (!MoneyUtil.equals(internalValue.value, modelValue?.value)) {
    const payload =
      internalValue.value?.amount !== undefined
        ? MoneyUtil.clone(internalValue.value)
        : undefined;
    emit('update:model-value', payload);
  }
};

const onCancel = () => {
  internalValue.value = MoneyUtil.clone(modelValue?.value);
};

const closePopup = () => {
  if (!popup.value) {
    return;
  }

  const popupRef = popup.value as unknown as typeof PopupInputgroup;
  popupRef.onCancel();
};

watch(
  modelValue,
  (newVal) => {
    internalValue.value = MoneyUtil.clone(newVal);
  },
  { immediate: true },
);
</script>
